import React, { useState } from "react";
import { TextField, Button, IconButton, InputAdornment, Typography } from "@mui/material";
import { Visibility, VisibilityOff, LockOutlined, EmailOutlined } from "@mui/icons-material";
import { useFormik } from "formik";
import * as Yup from "yup";
import isUserLoggedIn from "./isLoggedIn";
import { useChangePasswordMutation } from "../../../services/apis/auth"; // Adjust the import based on your API slice
import { hybridEncrypt, hybridDecrypt } from "../../features/authentication/encryption"; // Ensure correct import
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
const validationSchema = Yup.object({
    email: Yup.string().email("Invalid email address").required("Email is required"),
    oldPassword: Yup.string().min(6, "Must be at least 6 characters").required("Old password is required"),
    newPassword: Yup.string().min(6, "Must be at least 6 characters").required("New password is required"),
});

const ChangePassword = () => {
    const [showOldPassword, setShowOldPassword] = useState(false);
    const [showNewPassword, setShowNewPassword] = useState(false);
    const navigate = useNavigate();
    const [changePassword, { isLoading }] = useChangePasswordMutation();
    const formik = useFormik({
        initialValues: {
            email: "",
            oldPassword: "",
            newPassword: "",
        },
        validationSchema,
        onSubmit: async (values) => {
            try {
                const encryptedPayload = await hybridEncrypt({ email: values.email, oldPassword: values.oldPassword, newPassword: values.newPassword });

                const result = await changePassword({ encryptedData: encryptedPayload });
                if (!result?.data?.encryptedData) {
                    if (result?.data?.success === false) {
                        toast.success(result.data.message);

                    } else {
                        toast.error(result.error.data.message);

                    }
                } else {
                    const decryptedPayload = await hybridDecrypt(result?.data?.encryptedData);

                    if (decryptedPayload?.success) {
                        localStorage.removeItem("token");
                        localStorage.removeItem("user");
                        toast.success(decryptedPayload.message);

                        navigate("/auth/signin");
                    } else {
                        toast.error(result.error.data.message);
                        navigate("/auth/signin");
                    }
                }
            } catch (error) {
                toast("Something went wrong");
                navigate("/auth/signin");
            }
        },
    });


    return (
        <div className="flex flex-col items-center justify-center min-h-screen bg-gradient-to-br from-blue-50 to-gray-200 p-6">
            <div className="bg-white bg-opacity-80 backdrop-blur-md p-10 rounded-3xl shadow-xl max-w-md w-full text-center">
                <LockOutlined className="text-blue-500 text-5xl mb-3" />
                <Typography variant="h5" className="font-semibold text-gray-700">
                    Change Password
                </Typography>
                <Typography className="text-gray-500 text-sm mt-1 mb-5">
                    Secure your account by updating your password.
                </Typography>

                <form onSubmit={formik.handleSubmit}>
                    {/* Email Field */}
                    <TextField
                        label="Email Address"
                        type="email"
                        fullWidth
                        variant="outlined"
                        margin="normal"
                        {...formik.getFieldProps("email")}
                        error={formik.touched.email && Boolean(formik.errors.email)}
                        helperText={formik.touched.email && formik.errors.email}
                        InputProps={{
                            endAdornment: (
                                <InputAdornment position="end">
                                    <EmailOutlined />
                                </InputAdornment>
                            ),
                        }}
                    />

                    {/* Old Password Field */}
                    <TextField
                        label="Old Password"
                        type={showOldPassword ? "text" : "password"}
                        fullWidth
                        variant="outlined"
                        margin="normal"
                        {...formik.getFieldProps("oldPassword")}
                        error={formik.touched.oldPassword && Boolean(formik.errors.oldPassword)}
                        helperText={formik.touched.oldPassword && formik.errors.oldPassword}
                        InputProps={{
                            endAdornment: (
                                <InputAdornment position="end">
                                    <IconButton onClick={() => setShowOldPassword(!showOldPassword)} edge="end">
                                        {showOldPassword ? <VisibilityOff /> : <Visibility />}
                                    </IconButton>
                                </InputAdornment>
                            ),
                        }}
                    />

                    {/* New Password Field */}
                    <TextField
                        label="New Password"
                        type={showNewPassword ? "text" : "password"}
                        fullWidth
                        variant="outlined"
                        margin="normal"
                        {...formik.getFieldProps("newPassword")}
                        error={formik.touched.newPassword && Boolean(formik.errors.newPassword)}
                        helperText={formik.touched.newPassword && formik.errors.newPassword}
                        InputProps={{
                            endAdornment: (
                                <InputAdornment position="end">
                                    <IconButton onClick={() => setShowNewPassword(!showNewPassword)} edge="end">
                                        {showNewPassword ? <VisibilityOff /> : <Visibility />}
                                    </IconButton>
                                </InputAdornment>
                            ),
                        }}
                    />

                    {/* Change Password Button */}
                    <Button
                        type="submit"
                        variant="contained"
                        color="primary"
                        fullWidth
                        className="mt-6 py-3 shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-300"
                    >
                        Update Password
                    </Button>
                </form>
            </div>
        </div>
    );
};

export default isUserLoggedIn(ChangePassword);
